import type React from "react";
import {useMemo} from "react";
import {Box, Typography} from "@mui/material";
import {CustomDataGrid} from "~/components/data-grid/CustomDataGrid";
import type {
    ComplianceArtifact,
    ComplianceRequirementState,
} from "~/pages/supplier-360/types/Supplier360Types";
import {getEvidenceColumns} from "~/pages/supplier-360/tabs/SupplierCompliance/components/evidenceTable/evidenceColumns";
import {EvidenceNoRowsOverlay} from "~/pages/supplier-360/tabs/SupplierCompliance/components/evidenceTable/EvidenceNoRowsOverlay";

/**
 * The empty state's hint by record state — the design's two `emptyText`s. Missing / expired
 * records get the "blocks qualification" warning; any other record is nudged to upload.
 */
function emptyHint(state: ComplianceRequirementState): string {
    return state === 'missing' || state === 'expired'
        ? 'This record is unmet and blocks qualification.'
        : 'Upload the certificate, report or declaration to move this record out of pending.';
}

type EvidenceTableProps = {
    artifacts: ComplianceArtifact[];
    /** The record's own `state` — picks the empty-state hint. */
    recordState: ComplianceRequirementState;
    loading: boolean;
    isError: boolean;
};

/** The record's evidence files (§2.9) — client-side, the list is fetched whole. */
export const EvidenceTable: React.FC<EvidenceTableProps> = ({artifacts, recordState, loading, isError}) => {
    const columns = useMemo(() => getEvidenceColumns(), []);

    if (isError) {
        return <Typography color="text.secondary">Could not load evidence.</Typography>;
    }

    return (
        <Box sx={{height: 360}}>
            <CustomDataGrid
                layoutKey="supplier-360-compliance-evidence"
                rows={artifacts}
                columns={columns}
                loading={loading}
                disableRowSelectionOnClick
                getRowHeight={() => 72}
                showToolbar
                toolbarProps={{title: `${artifacts.length} file${artifacts.length === 1 ? '' : 's'}`, showFilterPanel: false}}
                slots={{noRowsOverlay: EvidenceNoRowsOverlay}}
                slotProps={{noRowsOverlay: {hint: emptyHint(recordState)}}}
            />
        </Box>
    );
};
